import {
  Box,
  Divider,
  Icon,
  InlineStack,
  LegacyStack,
  Pagination,
  ResourceList,
  Text,
  TextField,
  Thumbnail,
} from '@shopify/polaris'
import { SearchIcon } from '@shopify/polaris-icons'
import React, { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'

import { CustomerAddress } from '../delivery-details-edit-dialog/models'
import { AdaptiveModal } from './AdaptiveModal'
import { backendFetchResult } from './api'
import { GQLPaginator, GQLPaginatorPageInfo } from './gql-models-common'
import { DialogActivator, queryString, useDebounced } from './helpers'
import { personalInfoClassName } from './PersonalInfo'
import { PaymentMethod } from './subscription-models'

export interface CustomerResult {
  id: string
  restId: number
  email: string
  displayName: string
  image: string | null
  addresses: CustomerAddress[]
  paymentMethods: PaymentMethod[]
}

interface CustomerQueryResult {
  search: string
  results: CustomerResult[]
  pageInfo: GQLPaginatorPageInfo | null
  beforeCursor: string | null
  afterCursor: string | null
}

const searchCustomers = async (
  _: any,
  search: string,
  beforeCursor: string | null,
  afterCursor: string | null
): Promise<CustomerQueryResult> => {
  const query = queryString({ search, before: beforeCursor, after: afterCursor })
  const res = await backendFetchResult<GQLPaginator<CustomerResult>>(
    'GET',
    `/customers/search?${query}`
  )
  const results = res.status === 'success' && res.data ? res.data.nodes : []
  const pageInfo = res.data?.page ?? null
  return { search, results, pageInfo, beforeCursor, afterCursor }
}

const renderCustomerResult = (
  result: CustomerResult,
  onSelect: (result: CustomerResult) => any
) => {
  return (
    <ResourceList.Item
      id={result.id}
      media={<Thumbnail source={result.image || ''} alt="" size="small" />}
      onClick={() => onSelect(result)}
      verticalAlignment="center"
    >
      <h3 className={personalInfoClassName}>
        <Text as="span" fontWeight="semibold">
          {result.displayName}
        </Text>
        <p>{result.email}</p>
      </h3>
    </ResourceList.Item>
  )
}

interface ModalContentProps {
  search: string
  onSearchChange: (value: string) => void
  items: CustomerResult[]
  loading: boolean
  renderItem: (item: CustomerResult) => React.ReactNode
}

const ModalContent = ({
  search,
  onSearchChange,
  items,
  loading,
  renderItem,
}: ModalContentProps) => (
  <>
    <Box padding="400">
      <TextField
        label=""
        placeholder="Search"
        prefix={<Icon source={SearchIcon} />}
        value={search}
        onChange={onSearchChange}
        autoComplete="off"
      />
    </Box>
    <Divider />
    <ResourceList
      items={items}
      renderItem={renderItem}
      loading={loading}
      emptyState={
        <LegacyStack distribution="center">
          <p style={{ padding: '2rem' }}>No customers found</p>
        </LegacyStack>
      }
    />
  </>
)

export const CustomerModal = ({
  activator,
  onSelect,
}: {
  activator: DialogActivator
  onSelect: (result: CustomerResult) => any
}) => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search, 500)

  const [beforeCursor, setBeforeCursor] = useState<string | null>(null)
  const [afterCursor, setAfterCursor] = useState<string | null>(null)

  const [queryResult, setQueryResult] = useState<CustomerQueryResult>({
    search: '___',
    results: [],
    pageInfo: null,
    beforeCursor: null,
    afterCursor: null,
  })
  const swr = useSWR(
    activator.open ? ['customers', debouncedSearch, beforeCursor, afterCursor] : null,
    searchCustomers
  )

  const myRenderCustomerResult = useCallback(
    (result: CustomerResult) => renderCustomerResult(result, onSelect),
    [onSelect]
  )

  useEffect(() => {
    if (
      swr.data &&
      swr.data.search === search &&
      swr.data.beforeCursor === beforeCursor &&
      swr.data.afterCursor === afterCursor
    ) {
      setQueryResult(swr.data)
    }
  }, [swr.data, search, beforeCursor, afterCursor, setQueryResult])

  useEffect(() => {
    setBeforeCursor(null)
    setAfterCursor(null)
  }, [search])

  const loading =
    queryResult.search !== search ||
    queryResult.beforeCursor !== beforeCursor ||
    queryResult.afterCursor !== afterCursor

  const pagination =
    queryResult.results.length > 0 && queryResult.pageInfo
      ? {
          hasPrevious: queryResult.pageInfo.hasPreviousPage && !loading,
          hasNext: queryResult.pageInfo.hasNextPage && !loading,
          onPrevious: () => {
            setAfterCursor(null)
            setBeforeCursor(queryResult.pageInfo?.firstCursor ?? null)
          },
          onNext: () => {
            setBeforeCursor(null)
            setAfterCursor(queryResult.pageInfo?.lastCursor ?? null)
          },
        }
      : undefined

  return (
    <AdaptiveModal
      title="Select customer"
      open={activator.open}
      onClose={activator.close}
      footer={
        pagination && (
          <InlineStack align="center">
            <Pagination
              hasPrevious={pagination.hasPrevious}
              onPrevious={pagination.onPrevious}
              hasNext={pagination.hasNext}
              onNext={pagination.onNext}
            />
          </InlineStack>
        )
      }
    >
      <ModalContent
        search={search}
        onSearchChange={setSearch}
        items={queryResult.results}
        loading={loading}
        renderItem={myRenderCustomerResult}
      />
    </AdaptiveModal>
  )
}
